<template>
  <transition name="modal">
    <div class="modal-mask" v-if="show" @click="closeModal">
      <div class="modal-wrapper">
        <div 
          class="modal-container" 
          ref="modalRef"
          @click.stop
          @mousedown="startDrag"
          @mousemove="onDrag"
          @mouseup="stopDrag"
          @mouseleave="stopDrag"
        >
          <div class="modal-header" @mousedown="startDrag">
            <slot name="header">
              <h3>Settings</h3>
            </slot>
            <button type="button" class="modal-close-btn" @click="closeModal" @mousedown.stop aria-label="Close">
              <i class="fas fa-times"></i>
            </button>
          </div>
          <div class="modal-body">
            <slot name="body">
              <div class="settings-section">
                <h4>Timer Settings</h4>
                <p class="section-description">Choose values in seconds for the start and repeat timers.</p>
               
                <el-form label-position="top" label-width="250px" class="settings">
                  <el-form-item label="Start Timer">
                    <el-input-number v-model="initialTimer" :min="20" :max="200" :step="5"/>
                  </el-form-item>
                  <el-form-item label="Repeat Timer">
                    <el-input-number v-model="secondaryTimer" :min="10" :max="200" :step="5"/>
                  </el-form-item>
                </el-form>
              </div>

              <div class="settings-section">
                <h4>Display Settings</h4>
                <div class="settings-switches">
                  <div class="setting-switch">
                    <span class="setting-label">Show Explanations When Done</span>
                    <label class="toggle-switch">
                      <input type="checkbox" v-model="gameStore.showExplanationsWhenDone" />
                      <span class="slider"></span>
                    </label>
                  </div>
                  
                  <div class="setting-switch">
                    <span class="setting-label">Show Tiers When Done</span>
                    <label class="toggle-switch">
                      <input type="checkbox" v-model="gameStore.showTiersWhenDone" />
                      <span class="slider"></span>
                    </label>
                  </div>
                </div>
              </div>

          
            </slot>
          </div>
          <div class="modal-footer">
            <slot name="footer">
              <button @click="closeModal" class="cancel">Close</button>
              <button @click="resetToDefaults" class="reset">Reset to Defaults</button>
            
              <button @click="saveSettings" class="save">Save</button>
            </slot>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, watch, nextTick } from "vue";
import { useGameStore } from "@/stores/gameStore";

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:show", "update-timer"]);

const gameStore = useGameStore();
const modalRef = ref(null);

// Drag functionality
const isDragging = ref(false);
const dragStart = ref({ x: 0, y: 0 });
const modalPosition = ref({ x: 0, y: 0 });

// ✅ Load saved values or use defaults
const initialTimer = ref(Number(localStorage.getItem("initialTimer")) || gameStore.timerLength);
const secondaryTimer = ref(Number(localStorage.getItem("secondaryTimer")) || gameStore.secondaryTimerLength);

// Load saved display settings or use defaults
const storedShowExplanationsWhenDone = localStorage.getItem("showExplanationsWhenDone");
if (storedShowExplanationsWhenDone !== null) {
  gameStore.showExplanationsWhenDone = storedShowExplanationsWhenDone === "true";
} else {
  // Set default if no value stored
  gameStore.showExplanationsWhenDone = true;
}

const storedShowTiersWhenDone = localStorage.getItem("showTiersWhenDone");
if (storedShowTiersWhenDone !== null) {
  gameStore.showTiersWhenDone = storedShowTiersWhenDone === "true";
} else {
  // Set default if no value stored
  gameStore.showTiersWhenDone = true;
}

// Drag functions
const startDrag = (event) => {
  if (event.target.closest('.modal-body') || event.target.closest('.modal-footer')) {
    return; // Don't drag when clicking on body or footer
  }
  
  isDragging.value = true;
  dragStart.value = {
    x: event.clientX - modalPosition.value.x,
    y: event.clientY - modalPosition.value.y
  };
  
  // Add dragging class for visual feedback
  if (modalRef.value) {
    modalRef.value.classList.add('dragging');
  }
  
  event.preventDefault();
};

const onDrag = (event) => {
  if (!isDragging.value) return;
  
  // Use requestAnimationFrame for smoother movement
  requestAnimationFrame(() => {
    modalPosition.value = {
      x: event.clientX - dragStart.value.x,
      y: event.clientY - dragStart.value.y
    };
    
    if (modalRef.value) {
      modalRef.value.style.transform = `translate(${modalPosition.value.x}px, ${modalPosition.value.y}px)`;
    }
  });
};

const stopDrag = () => {
  isDragging.value = false;
  
  // Remove dragging class
  if (modalRef.value) {
    modalRef.value.classList.remove('dragging');
  }
};

// ✅ Close modal
const closeModal = () => {
  // Reset position when closing
  modalPosition.value = { x: 0, y: 0 };
  if (modalRef.value) {
    modalRef.value.style.transform = 'translate(0, 0)';
  }
  emit("update:show", false);
};

// ✅ Reset to default values
const resetToDefaults = () => {
  initialTimer.value = 120; // Default start timer (2 minutes)
  secondaryTimer.value = 60; // Default repeat timer (1 minute)
  gameStore.showExplanationsWhenDone = true; // Default: show explanations
  gameStore.showTiersWhenDone = true; // Default: show tiers
};

// ✅ Save settings & update game timer
const saveSettings = () => {
  localStorage.setItem("initialTimer", initialTimer.value);
  localStorage.setItem("secondaryTimer", secondaryTimer.value);

  gameStore.updateTimer(initialTimer.value); // ✅ Update the game timer
  emit("update-timer", initialTimer.value); // Emit event if needed

  gameStore.updateSecondaryTimer(secondaryTimer.value);
  emit("update-secondary-timer", secondaryTimer.value); // Emit event if needed

  closeModal();
};

// ✅ Watch for external timer updates (ensures settings reflect the latest value)
watch(() => gameStore.timerLength, (newVal) => {
  initialTimer.value = newVal;
});

// Watch for display settings changes and save to localStorage
watch(() => gameStore.showExplanationsWhenDone, (newVal) => {
  localStorage.setItem("showExplanationsWhenDone", newVal);
});

watch(() => gameStore.showTiersWhenDone, (newVal) => {
  localStorage.setItem("showTiersWhenDone", newVal);
});

// Reset position when modal opens
watch(() => props.show, (newVal) => {
  if (newVal) {
    nextTick(() => {
      modalPosition.value = { x: 0, y: 0 };
      if (modalRef.value) {
        modalRef.value.style.transform = 'translate(0, 0)';
      }
    });
  }
});
</script>

<style src="../styles/_modals.scss" lang="scss"></style>
